import type React from "react";
import { useRef, useState, useEffect, useCallback } from "react";
import styles from "src/components/layout/SegmentedControl/segmentedControl.module.css";

const generateRefs = (count: number) => {
    return Array.from({ length: count }, () => {
        return useRef(null);
    });
};

type SegmentedControlProps = {
    name: string;
    segments: readonly { label: string; value: string }[];
    callback: (value: string, index: number) => void;
    // eslint-disable-next-line react/require-default-props
    defaultIndex?: number;
    controlRef: any;
};

const SegmentedControl: React.FC<SegmentedControlProps> = ({
    name,
    segments,
    callback,
    defaultIndex = 0,
    controlRef,
}) => {
    const [activeIndex, setActiveIndex] = useState(defaultIndex);
    const componentReady = useRef(false);
    const refs = generateRefs(segments.length);

    const addRefToOptions = useCallback(() => {
        const initialTopPageSegmentOptions = segments;
        return initialTopPageSegmentOptions.map((option) => {
            return {
                ...option,
                ref: refs[initialTopPageSegmentOptions.indexOf(option)],
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const options: { label: string; value: string; ref: any }[] =
        addRefToOptions();

    useEffect(() => {
        componentReady.current = true;
    }, []);

    useEffect(() => {
        const activeSegmentRef = options[activeIndex].ref;
        const { offsetWidth, offsetLeft } = activeSegmentRef.current;
        const { style } = controlRef.current;

        style.setProperty("--highlight-width", `${offsetWidth}px`);
        style.setProperty("--highlight-x-pos", `${offsetLeft}px`);
    }, [activeIndex, callback, controlRef, options, segments]);

    const onInputChange = (value: string, index: number) => {
        setActiveIndex(index);
        callback(value, index);
    };

    return (
        <div className={styles.controls_container} ref={controlRef}>
            <div
                className={`${styles.controls} ${
                    componentReady.current ? styles.ready : "idle"
                } ${options.length > 2 ? styles.controls_space_between : ""}`}
            >
                {options?.map((item, i) => {
                    return (
                        <div
                            key={item.value}
                            className={`${styles.segment} ${
                                i === activeIndex ? styles.active : "inactive"
                            }`}
                            ref={item.ref}
                        >
                            <input
                                type="radio"
                                value={item.value}
                                id={item.label}
                                name={name}
                                onChange={() => {
                                    return onInputChange(item.value, i);
                                }}
                                checked={i === activeIndex}
                            />
                            <label htmlFor={item.label}>{item.label}</label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SegmentedControl;
